/**
 * StartReviewForm — Full-page BaseForm for starting a review session.
 *
 * Replaces the old StartReviewModal with a full-page form aligned to
 * MatterForm and CreateTableForm patterns. Uses BaseForm for consistent
 * validation and layout.
 *
 * URL: /reviews?add=true[&playbook=<uuid>]
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import Badge from '../shared/TailwindComponents/Badge';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { startReview } from '../../store/actions/Review/ReviewActions';
import { fetchMatterDropdowns } from '../../store/actions/Matters/MatterActions';
import { fetchDataSources } from '../../store/actions/AIStudioAction';
import { PlaybookListItem } from '../../types/playbook';
import apis from '../../utils/apis';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, startReviewFormTourSteps } from '../shared/FeatureTour';

// ─── Custom field: Playbook selector ─────────────────────────────────────────

interface PlaybookSelectorProps {
  value: string | null;
  onChange: (value: string) => void;
  playbooks: PlaybookListItem[];
  isLoading: boolean;
  t: (key: string) => string;
}

const PlaybookSelector: React.FC<PlaybookSelectorProps> = ({
  value,
  onChange,
  playbooks,
  isLoading,
  t,
}) => {
  if (isLoading) {
    return <div className="text-sm text-gray-400 py-2">{t('Loading playbooks...')}</div>;
  }

  if (playbooks.length === 0) {
    return (
      <div className="text-sm text-gray-400 py-2">
        {t('No playbooks found. Create a playbook in Templates first.')}
      </div>
    );
  }

  return (
    <div className="space-y-1 max-h-48 overflow-y-auto">
      {playbooks.map((pb) => (
        <button
          key={pb.uuid}
          type="button"
          onClick={() => onChange(value === pb.uuid ? '' : pb.uuid)}
          className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm ${
            value === pb.uuid
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">{pb.name}</span>
            <span className="text-xs text-gray-400">
              {pb.clause_count} {t('clauses')} · {pb.global_rule_count} {t('rules')}
            </span>
          </div>
          {pb.agreement_types?.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {pb.agreement_types.slice(0, 3).join(', ')}
              {pb.agreement_types.length > 3 && ` +${pb.agreement_types.length - 3}`}
            </p>
          )}
        </button>
      ))}
    </div>
  );
};

// ─── Custom field: Data Source selector ───────────────────────────────────────

interface DataSourceSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  dataSources: Array<{ uuid: string; name: string; description?: string; status?: string; document_count?: number }>;
  isLoading: boolean;
  t: (key: string) => string;
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  value,
  onChange,
  dataSources,
  isLoading,
  t,
}) => {
  const safeValue = value || [];

  const toggle = (uuid: string) => {
    onChange(
      safeValue.includes(uuid) ? safeValue.filter((id) => id !== uuid) : [...safeValue, uuid],
    );
  };

  if (isLoading) {
    return <div className="text-sm text-gray-400 py-2">{t('Loading data sources...')}</div>;
  }

  if (dataSources.length === 0) {
    return (
      <div className="text-sm text-gray-400 py-2">
        {t('No data sources available. You can create them in AI Studio.')}
      </div>
    );
  }

  return (
    <div className="space-y-1 max-h-36 overflow-y-auto">
      {dataSources.map((ds) => (
        <button
          key={ds.uuid}
          type="button"
          onClick={() => toggle(ds.uuid)}
          className={`w-full text-left px-3 py-2 rounded-lg border transition-all text-sm ${
            safeValue.includes(ds.uuid)
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">{ds.name}</span>
            <div className="flex items-center gap-1.5">
              {ds.document_count !== undefined && (
                <Badge color="gray">{ds.document_count} docs</Badge>
              )}
              {ds.status && (
                <Badge color={ds.status === 'ready' ? 'green' : 'gray'}>
                  {ds.status}
                </Badge>
              )}
            </div>
          </div>
          {ds.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
              {ds.description}
            </p>
          )}
        </button>
      ))}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const StartReviewForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();

  // Pre-select playbook from URL param (e.g. /reviews?add=true&playbook=<uuid>)
  const preselectedPlaybook = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('playbook') || '';
  }, [location.search]);

  const isUploading = useSelector((s: RootState) => (s as any).review?.isUploading ?? false);

  // Data sources from Redux (populated by fetchDataSources dispatch)
  const reduxDataSources = useSelector((s: RootState) => (s as any).aistudio?.indexes || []);
  const reduxDsLoading = useSelector((s: RootState) => (s as any).aistudio?.loading ?? false);

  // Filter to only ready/indexed data sources
  const readyDataSources = useMemo(
    () =>
      reduxDataSources.filter((ds: any) => {
        const idxStatus = (ds.indexer_status || '').toLowerCase();
        const dsStatus = (ds.status || '').toLowerCase();
        return (
          ['success', 'completed'].includes(idxStatus) ||
          ['ready', 'active'].includes(dsStatus)
        );
      }),
    [reduxDataSources],
  );

  // Data state
  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(true);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<DropdownOption[]>([]);
  const [languageOptions, setLanguageOptions] = useState<DropdownOption[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [defaultJurisdiction, setDefaultJurisdiction] = useState<string>('');

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'start-review-form',
    steps: startReviewFormTourSteps,
    readyToShow: !isLoadingPlaybooks,
    autoStartDelay: 600,
  });

  // ─── Fetch dropdown data ────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      // Fetch playbooks
      setIsLoadingPlaybooks(true);
      try {
        const response = await apis.get('/playbooks', { params: { page: 1, page_size: 100 } });
        if (response.status === 200) {
          setPlaybooks(response.data.items || []);
        }
      } catch {
        // Silent — user sees empty list
      } finally {
        setIsLoadingPlaybooks(false);
      }

      // Fetch jurisdictions and languages (with AI settings defaults)
      try {
        const dropdownsData = await dispatch(fetchMatterDropdowns() as any);
        if (dropdownsData?.jurisdictions) {
          setJurisdictionOptions(dropdownsData.jurisdictions);
        }
        if (dropdownsData?.languages) {
          setLanguageOptions(dropdownsData.languages);
        }
        // Use AI settings as default selected values
        const aiSettings = dropdownsData?.aiSettings;
        if (aiSettings) {
          if (aiSettings.language) setDefaultLanguage(aiSettings.language);
          if (aiSettings.jurisdiction) setDefaultJurisdiction(aiSettings.jurisdiction);
        }
      } catch {
        // Silent — jurisdiction/language will fallback to empty
      }

      // Fetch data sources → stored in Redux (state.aistudio.indexes)
      dispatch(fetchDataSources({ page: 1, limit: 100 }) as any);
    };

    loadData();
  }, [dispatch]);

  // ─── Form schema ───────────────────────────────────────────────────

  const schema = useMemo(
    () =>
      new FormSchema('startReview', [
        new FormField('name', 'text', false, t('Review Name'), {
          customValidator: (value: any) => {
            if (value && typeof value === 'string' && value.trim().length > 200) {
              return { isValid: false, error: t('Name must be 200 characters or fewer') };
            }
            return { isValid: true, sanitized: value };
          },
        }),
        new FormField('playbook', 'text', true, t('Playbook'), {
          customValidator: (value: any) => {
            if (!value || (typeof value === 'string' && !value.trim())) {
              return { isValid: false, error: t('Select a playbook to review against') };
            }
            return { isValid: true, sanitized: value };
          },
        }),
        new FormField('documents', 'text', true, t('Documents'), {
          customValidator: (value: any) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return { isValid: false, error: t('Upload at least one document to review') };
            }
            return { isValid: true, sanitized: value };
          },
        }),
        // new FormField('jurisdiction', 'text', false, t('Jurisdiction'), {
        //   customValidator: () => ({ isValid: true }),
        // }),
        new FormField('language', 'text', false, t('Language'), {
          customValidator: () => ({ isValid: true }),
        }),
        // new FormField('data_sources', 'text', false, t('Data Sources'), {
        //   customValidator: () => ({ isValid: true }),
        // }),
        new FormField('collaborators', 'text', false, t('Access'), {
          customValidator: () => ({ isValid: true }),
        }),
      ]),
    [t],
  );

  // ─── Form sections ─────────────────────────────────────────────────

  const sections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        title: t('Review Details'),
        description: t('Give your review session a name to easily identify it later.'),
        dataTourId: 'start-review-name',
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Review Name'),
            placeholder: t('e.g. Q4 Vendor Agreement Review'),
            required: false,
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Access'),
        description: t('Grant access to people who should collaborate on this review.'),
        dataTourId: 'start-review-collaborators',
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: t('Access'),
            required: false,
            gridSize: 'full',
            customProps: {
              allowedPermissions: ['edit', 'view'],
              showOwnerSection: true,
              ownerLabel: t('You are the review owner (admin)'),
            },
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Select Playbook'),
        description: t('Choose a playbook with the clause rules and global rules to review against.'),
        dataTourId: 'start-review-playbook',
        fields: [
          {
            name: 'playbook',
            type: 'custom',
            label: t('Playbook'),
            required: true,
            gridSize: 'full',
            customComponent: PlaybookSelector,
            customProps: { playbooks, isLoading: isLoadingPlaybooks, t },
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Documents to Review'),
        description: t('Upload the contracts or documents you want to review. Each document will be analyzed against the playbook rules.'),
        dataTourId: 'start-review-documents',
        fields: [
          {
            name: 'documents',
            type: 'fileUploader',
            label: t('Documents'),
            required: true,
            gridSize: 'full',
            maxFiles: 20,
            allowedExtensions: ['pdf', 'docx', 'doc', 'txt'],
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Language'),
        description: t('Set output language.'),
        dataTourId: 'start-review-language',
        gridCols: 2,
        fields: [
          {
            name: 'language',
            type: 'dropdown',
            label: t('AI Output Language'),
            options: languageOptions,
            placeholder: t('Select language'),
            required: false,
            customProps: { fullWidth: true },
            description: t('Language for AI-generated findings, suggestions, and redlines.'),
          } as BaseFormFieldConfig,
          // {
          //   name: 'jurisdiction',
          //   type: 'dropdown',
          //   label: t('Jurisdiction'),
          //   options: jurisdictionOptions,
          //   placeholder: t('Select jurisdiction (optional)'),
          //   required: false,
          //   gridSize: 'half',
          //   customProps: { multiple: true, fullWidth: true },
          // } as BaseFormFieldConfig,
          // ...(readyDataSources.length > 0 || reduxDsLoading
          //   ? [
          //       {
          //         name: 'data_sources',
          //         type: 'custom',
          //         label: t('Data Sources'),
          //         required: false,
          //         gridSize: 'full',
          //         customComponent: DataSourceSelector,
          //         customProps: { dataSources: readyDataSources, isLoading: reduxDsLoading, t },
          //         description: t('Connect data sources to check against local laws, regulations, or firm standards.'),
          //       } as BaseFormFieldConfig,
          //     ]
          //   : []),
        ],
      },
    ],
    [t, playbooks, isLoadingPlaybooks, languageOptions],
  );

  // ─── Submit ─────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      if (!checkAiFeatureGate('review_ai')) return;
      const playbookUuid = formData.playbook;
      const files = formData.documents;
      // const jurisdiction = formData.jurisdiction;
      // const dataSourceIds = formData.data_sources;
      const reviewName = formData.name;
      const language = formData.language;
      const collaborators: Collaborator[] = formData.collaborators || [];

      if (!playbookUuid || !files?.length) return;

      const result = await dispatch(
        startReview(playbookUuid, {
          files: Array.isArray(files) ? files : [files],
          name: reviewName?.trim() || undefined,
          // jurisdiction: Array.isArray(jurisdiction) && jurisdiction.length > 0 ? jurisdiction : undefined,
          // data_source_ids: Array.isArray(dataSourceIds) && dataSourceIds.length > 0 ? dataSourceIds : undefined,
          language: language || undefined,
          collaborators: collaborators.length > 0 ? collaborators : undefined,
        }) as any,
      );

      const reviewUuid = result?.session_uuid || result?.review_uuid;
      if (reviewUuid) {
        history.push(`/reviews/${reviewUuid}`);
      }
    },
    [dispatch, history],
  );

  const handleCancel = useCallback(() => {
    history.push('/reviews');
  }, [history]);

  // ─── Breadcrumbs ────────────────────────────────────────────────────

  const breadcrumbs = useMemo(
    () => [
      { name: t('Reviews'), href: '/reviews', current: false },
      { name: t('New Review'), href: '/reviews?add=true', current: true },
    ],
    [t],
  );

  return (
    <>
      <BaseForm
        title={t('Start Contract Review')}
        description={t(
          'Select a playbook and upload documents to review against your firm standards, clause rules, and global rules.',
        )}
        sections={sections}
        schema={schema}
        initialValues={{
          name: '',
          playbook: preselectedPlaybook,
          documents: [],
          language: defaultLanguage || '',
          // jurisdiction: defaultJurisdiction ? [defaultJurisdiction] : [],
          // data_sources: [],
          collaborators: [],
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel={isUploading ? t('Starting...') : t('Start Review')}
        cancelLabel={t('Cancel')}
        isSubmitting={isUploading}
        submitDisabled={!hasAiFeatureAccess('review_ai')}
        submitTooltip={t('Upgrade your plan to use AI Review.')}
      />

      <FeatureTour
        steps={startReviewFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </>
  );
};

export default StartReviewForm;
